import { flow, types } from 'mobx-state-tree';

import api from 'services/API';
import { getRootStore } from './root';
import { Condition } from './types';
import { dateUtilities, stringUtilities } from 'utils';
import { ALARMS_TYPES, HOME_CONDITION_TYPES, ALARM_OFFLINE_TYPES } from 'config/alarmsType';

const ConditionWithView = Condition.views(self => ({
  get alarm() {
    const root = getRootStore();
    return root.notificationsStore.alarms.find(({ id, type, troubleshooting }) => {
      if (id !== self.alarm_id) return false;
      if (!ALARM_OFFLINE_TYPES.includes(type)) return true;
      return Boolean(troubleshooting);
    });
  },
  get gateway() {
    return getRootStore().deviceHistoryStore.sortedGateways.find(
      g => g.id === self.context.gateway_id || g.id === self.alarm?._gateways_id,
    );
  },
  get troubleshootType() {
    if (self.type.includes('Temperature Sensor') || self.type.includes('Flow Sensor')) return 'bru';

    if (self.type === 'System Offline') {
      if (!self.context?.offline_gateways) return null;

      const gatewaysIds = Object.keys(self.context.offline_gateways).map(Number);

      const gateway = getRootStore().topologyManagementStore.gateways.find(g =>
        gatewaysIds.includes(g.id),
      );

      if (!gateway) return null;

      if (gateway.identifier.includes('bcm')) return 'bcm';

      return 'gw';
    }

    return null;
  },
}));

export const conditionsInitialState = {
  isLoaded: false,
  all: [],
  selectedConditionStateFilter: null,
  state: 'done',
  conditionsFilterValue: null,
};

export const conditionsModel = types
  .model({
    isLoaded: types.boolean,
    all: types.array(ConditionWithView),
    conditionsFilterValue: types.maybeNull(
      types.enumeration('selectedConditionStateFilter', ['active', 'resolved']),
    ),
  })
  .views(self => ({
    get allConditions() {
      return self.all.slice();
    },
    get allActiveConditions() {
      return self.all.filter(
        condition => HOME_CONDITION_TYPES.includes(condition.type) && !condition._closed,
      );
    },
    get filteredConditions() {
      let conditions = [];
      if (self.conditionsFilterValue === null) {
        conditions = [...self.all];
      } else if (self.conditionsFilterValue === 'active') {
        conditions = [...self.all.filter(c => !c._closed)];
      } else if (self.conditionsFilterValue === 'resolved') {
        conditions = [...self.all.filter(c => c._closed)];
      }
      return conditions.sort((a, b) => Date.parse(b.happened_from) - Date.parse(a.happened_from));
    },
    get personalizedConditions() {
      const root = getRootStore();
      const alarmsIds = root.alarmsStore.availableAlarms
        .filter(alarm => alarm.role_users_ids.includes(root.userStore.currentRole?._role_id))
        .map(alarm => alarm.id);
      return self.all.filter(c => alarmsIds.includes(c.alarm_id));
    },

    getActiveConditionsByGatewayId(gateway_id) {
      return self.all.filter(item => item.context.gateway_id === gateway_id && !item._closed);
    },
  }))
  .actions(self => ({
    fetchConditions: flow(function* () {
      try {
        self.state = 'pending';
        const root = getRootStore();

        let { from, to } = root.deviceHistoryStore.period;

        if (!from || !to) {
          ({ from, to } = dateUtilities.getDatesByPeriod('1'));
        }

        const response = yield api.getConditions({
          'from[happened_from]': from,
          'to[happened_from]': to,
        });

        if (response?.data?.result) {
          self.all.replace(response.data.result);
          self.state = 'done';
          self.isLoaded = true;
          self.updated = new Date();
          return response.data.result;
        } else {
          self.isLoaded = false;
          self.state = 'done';
          self.updated = new Date();
        }
      } catch (error) {
        self.isLoaded = false;
        self.state = 'error';
        self.updated = new Date();
        console.error(error);
        return Promise.reject(error);
      }
    }),

    closeConditions: flow(function* (conditionIds) {
      if (!conditionIds?.length) return;

      const response = yield api.closeConditions({ condition_ids: conditionIds });

      if (response?.data?.result) {
        self.setConditionsClosed(conditionIds);
      }
    }),

    setConditionsClosed(conditionIds) {
      (conditionIds || []).forEach(id => {
        const condition = self.all.find(item => item.id === id);
        if (condition) {
          condition._closed = true;
          condition.happened_to = new Date().toISOString();
        }
      });
    },

    createSupportTicket: flow(function* (data) {
      try {
        yield api.createSupportTicket(data);
      } catch (error) {
        self.isLoaded = false;

        console.error(error);
        return Promise.reject(error);
      }
    }),

    setConditions(conditions) {
      self.all.replace(
        conditions.map(condition => ({
          ...condition,
          criteria_value: stringUtilities.ruleToValue(condition.criteria),
        })),
      );
    },

    getConditionById: flow(function* (id) {
      try {
        const response = yield api.getConditionById(id);
        return response.data.result;
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      }
    }),

    upsertCondition(condition) {
      if (condition._alarms_operand_id) {
        condition._alarms_operand_id = Number(condition._alarms_operand_id);
      }
      const index = self.all.findIndex(c => c.id === condition.id);
      if (index !== -1) {
        self.all[index] = condition;
      } else if (condition.type === ALARMS_TYPES.LINE_CLEANING_DETECTED) {
        self.all.unshift(condition);
      } else {
        self.all.push(condition);

        const sameTypeOpenedConditionIndex = self.all.findIndex(
          c => c.id !== condition.id && c.type === condition.type && !c.happened_to,
        );

        if (sameTypeOpenedConditionIndex !== -1) {
          self.all[sameTypeOpenedConditionIndex].happened_to = condition.happened_from;
          self.all[sameTypeOpenedConditionIndex]._closed = true;
        }
      }

      getRootStore().hardwareStatusStore.upsertOfflineCondition(condition);
    },

    setConditionsFilterValue(value) {
      self.conditionsFilterValue = value;
    },
  }));
